import type {
  ComponentVisibility,
  FormComponent,
  FormDSL,
  FormModule,
  FormStep,
} from '@commenda/types';
import { FormsTemplateType } from '@commenda/types';

/** Shape helpers for a linked form template, shared by its renderer and its completeness check. */
export class LinkedFormDslHelper {
  /**
   * Linked templates may be saved as a MODULE or STEP (no top-level `steps`). The renderer, the
   * Commenda OS hooks and the completeness check all read steps[].modules[].components[].
   */
  static normalize(dsl: unknown, type: FormsTemplateType): FormDSL {
    if (type === FormsTemplateType.FORM_MODULE) {
      // Stored templates are untyped JSON; a MODULE template is a FormModule by contract.
      return LinkedFormDslHelper.wrapModule(dsl as FormModule);
    }
    if (type === FormsTemplateType.FORM_STEP) {
      // As above: a STEP template is a FormStep by contract.
      return LinkedFormDslHelper.wrapSteps([dsl as FormStep]);
    }
    // As above: a COMPLETE_FORM template is a FormDSL by contract.
    return dsl as FormDSL;
  }

  static wrapModule(module: FormModule): FormDSL {
    return LinkedFormDslHelper.wrapSteps([
      { id: 'linked-step-1', title: '', order: 0, modules: [module] },
    ]);
  }

  /**
   * Whether a step, module or field shows for these answers. CheckboxGroup answers are
   * `{ option: boolean }` maps, so they are matched by option key rather than by value.
   */
  static isVisible(
    visibility: ComponentVisibility | undefined,
    values: Readonly<Record<string, unknown>>,
    componentMap: Readonly<Record<string, FormComponent>>
  ): boolean {
    if (!visibility) return true;
    return LinkedFormDslHelper.isConditionMet(
      visibility.condition,
      values,
      componentMap
    );
  }

  private static isConditionMet(
    condition: ComponentVisibility['condition'],
    values: Readonly<Record<string, unknown>>,
    componentMap: Readonly<Record<string, FormComponent>>
  ): boolean {
    if (
      condition.field &&
      condition.operator &&
      (condition.value !== undefined || condition.operator === 'isNotEmpty')
    ) {
      const fieldValue = values[condition.field];
      if (
        componentMap[condition.field]?.component === 'CheckboxGroup' &&
        typeof fieldValue === 'object' &&
        fieldValue !== null &&
        !Array.isArray(fieldValue)
      ) {
        return LinkedFormDslHelper.isCheckboxConditionMet(
          condition,
          // Narrowed above: a CheckboxGroup answer is an option → checked map.
          fieldValue as Record<string, boolean>
        );
      }
      return LinkedFormDslHelper.isValueConditionMet(condition, fieldValue);
    }

    if (condition.and) {
      return condition.and.every((cond) =>
        LinkedFormDslHelper.isConditionMet(cond, values, componentMap)
      );
    }
    if (condition.or) {
      return condition.or.some((cond) =>
        LinkedFormDslHelper.isConditionMet(cond, values, componentMap)
      );
    }
    return true;
  }

  private static isCheckboxConditionMet(
    condition: ComponentVisibility['condition'],
    checkboxValue: Readonly<Record<string, boolean>>
  ): boolean {
    const optionKey = String(condition.value);
    switch (condition.operator) {
      case 'equals':
        return checkboxValue[optionKey] === true;
      case 'notEquals':
        return checkboxValue[optionKey] !== true;
      case 'contains':
        return Object.entries(checkboxValue).some(
          ([key, checked]) => checked === true && key === optionKey
        );
      case 'isNotEmpty':
        return Object.values(checkboxValue).some((checked) => checked === true);
      default:
        return true;
    }
  }

  private static isValueConditionMet(
    condition: ComponentVisibility['condition'],
    fieldValue: unknown
  ): boolean {
    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'notEquals':
        return fieldValue !== condition.value;
      case 'greaterThan':
        return Number(fieldValue) > Number(condition.value);
      case 'lessThan':
        return Number(fieldValue) < Number(condition.value);
      case 'contains':
        if (Array.isArray(fieldValue)) {
          return fieldValue.some((item) => item === condition.value);
        }
        return String(fieldValue ?? '').includes(String(condition.value));
      case 'isNotEmpty':
        return Array.isArray(fieldValue)
          ? fieldValue.length > 0
          : fieldValue !== null &&
              fieldValue !== undefined &&
              fieldValue !== '';
      default:
        return true;
    }
  }

  private static wrapSteps(steps: FormStep[]): FormDSL {
    return {
      version: '1',
      form: { id: 'linked', title: '', type: 'single-page', settings: {} },
      steps,
    };
  }
}
